import { useCallback } from 'react';

import { useStore } from '@feathr/extender/state';
import type { IRachisMessage } from '@feathr/rachis';

import type { IDashboardUrlState, TDateRange } from '../ReportsConversionsDashboardPage.utils';
import { buildExportParams } from './AllConversionsTable.utils';

export interface IUseExportParams {
  state: IDashboardUrlState;
  range: TDateRange;
}

export interface IUseExportReturn {
  handleExport: (email: string) => Promise<IRachisMessage | Error>;
}

/**
 * Wraps `Conversions.export(...)` so the resolved type matches what
 * `ExportModal.onConfirm` expects: a resolved value of `IRachisMessage` on
 * success or `Error` on failure (the modal toasts based on `instanceof Error`).
 *
 * The narrowing in the catch is the standard TS pattern — `catch (e)` types
 * `e` as `unknown` since TS 4.0, so we narrow before returning.
 *
 * Known limitation: the BE silently no-ops account-scoped exports until the
 * integration ticket §2 lands (the worker raises `ValueError` for the
 * missing campaign/flight/event arg but the HTTP view returns 200 first, so
 * the success toast fires regardless). Track via the follow-up integration
 * ticket; do not work around in this file.
 */
export function useExport({ state, range }: IUseExportParams): IUseExportReturn {
  const { Conversions } = useStore();

  const handleExport = useCallback(
    async (email: string): Promise<IRachisMessage | Error> => {
      try {
        return await Conversions.export(buildExportParams(state, range, email));
      } catch (e) {
        return e instanceof Error ? e : new Error('Export failed');
      }
    },
    [Conversions, state, range],
  );

  return { handleExport };
}
